import { useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import { CircularProgress } from '@mui/material';
import useOffers from 'api/useOffers';
import useOffersUI from 'api/useOffersUI';
import { OfferUI } from 'common/contracts';
import { OfferType } from 'constants/enums';
import { EToasterStatus, showToast } from 'utils/showToast';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';

import DialogModal from 'components/DialogModal/DialogModal';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';

const EditOfferDesignModal = ({
  offerId,
  offerDesignType,
  onClose,
  isOpen,
  offerType
}: {
  offerId: string;
  offerDesignType: OfferDesignTypes;
  onClose: () => void;
  isOpen: boolean;
  offerType: OfferType;
}) => {
  const { publisherId } = useParams();
  const { getOffers, getOffer, updateOfferDesign } = useOffers({
    offerId,
    offerType
  });
  const { getOffersUI } = useOffersUI(publisherId);

  const isLoading = getOffer.isLoading || getOffersUI.isLoading;
  const [selectedOfferUI, setSelectedOfferUI] = useState<
    | {
        label: string;
        value: string;
        externalId: string;
      }
    | undefined
  >(undefined);

  const currentOfferUI = useMemo(() => {
    if (getOffer.isLoading || !getOffer.data?.result) return undefined;

    return {
      label: getOffer.data.result.offerUi.name,
      value: getOffer.data.result.offerUi.offerUiId,
      externalId: getOffer.data.result.offerUi.externalId
    };
  }, [getOffer.isLoading, getOffer.data?.result]);

  const offersUI = useMemo(() => {
    if (getOffersUI.isLoading || !getOffersUI.data?.result) return [];

    return getOffersUI.data.result
      .filter((oi: OfferUI) => oi.offerUiType === offerDesignType)
      .map((offerUI: OfferUI) => ({
        label: offerUI.name,
        value: offerUI._id,
        externalId: offerUI.externalId
      }));
  }, [getOffersUI.isLoading, getOffersUI.data?.result, offerDesignType]);

  useEffect(() => {
    if (currentOfferUI && !selectedOfferUI) {
      setSelectedOfferUI(currentOfferUI);
    }
  }, [currentOfferUI, selectedOfferUI]);

  const handleSave = () => {
    if (!selectedOfferUI?.value) return;

    updateOfferDesign.mutate(
      {
        offerId,
        offerDesignId: selectedOfferUI.value
      },
      {
        onSuccess: () => {
          showToast({
            message: 'Offer design updated successfully',
            status: EToasterStatus.SUCCESS
          });
          getOffers.refetch();
          onClose();
        },
        onError: () => {
          showToast({
            message: 'Error updating offer design',
            status: EToasterStatus.ERROR
          });
        }
      }
    );
  };

  return (
    <DialogModal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Edit Offer"
      description="Customize the design of your offer"
      modalActions={{
        onConfirm: {
          label: 'Save',
          action: handleSave,
          disabled:
            isLoading || selectedOfferUI?.value === currentOfferUI?.value
        },
        onClose: {
          label: 'Cancel',
          action: onClose,
          variant: 'link'
        }
      }}
    >
      {isLoading ? (
        <div className="w-full flex justify-center">
          <CircularProgress className="h-16 w-16" />
        </div>
      ) : (
        <InputSelectDropdown
          label="Offer Design"
          options={offersUI}
          defaultSelected={currentOfferUI}
          onOptionChange={(value) => {
            const selected = offersUI.find(
              (option: any) => option.value === value
            );
            setSelectedOfferUI(selected);
          }}
        />
      )}
    </DialogModal>
  );
};

export default EditOfferDesignModal;
